import { Link, useLocation } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { UnderlinedTag } from "./UnderlinedTag";
import { getThemeForPath } from "../config/theme";
import { getParentRoute } from "../config/projects";

/**
 * Unified page title component that replaces both PageHeader and ArticleTitle
 * Supports optional subtitle (plain text or link), back links, and dynamic structures
 * 
 * @param {string} title - The main page title
 * @param {string|false} subtitle - Optional subtitle text (or false to hide)
 * @param {string|false} subtitleLink - Optional link destination for subtitle (makes subtitle clickable)
 * @param {string|false|null} backLink - Optional back link destination (renders a back button/link).
 *                                      - `false` to explicitly disable.
 *                                      - `null` or `undefined` to auto-detect from hierarchy.
 *                                      - String for custom path.
 * @param {boolean} showBacklink - Whether to show the back link (default: true). Overrides backLink logic.
 * @param {string|null} colorClass - Override theme color (auto-detected from route if not provided)
 * @param {boolean} enableUnderline - Whether to show colored underline (default: true)
 */
export const PageTitle = ({ 
    title, 
    subtitle = false, 
    subtitleLink = false,
    backLink = null, // null = auto-detect, false = no link, string = explicit link
    showBacklink = true, // Whether to show back link (default: true)
    colorClass = null, 
    enableUnderline = true 
}) => {
    const location = useLocation();
    
    // Use provided colorClass, or auto-detect from route, or no color
    const themeColor = colorClass || getThemeForPath(location.pathname);
    
    // Determine back link: showBacklink flag > explicit > auto-detect > none
    let effectiveBackLink = null;
    if (!showBacklink) {
        // Explicitly disabled via showBacklink flag
        effectiveBackLink = null;
    } else if (backLink === false) {
        // Explicitly disabled
        effectiveBackLink = null;
    } else if (backLink !== null && backLink !== undefined) {
        // Explicitly provided
        effectiveBackLink = backLink;
    } else {
        // Auto-detect from hierarchy
        effectiveBackLink = getParentRoute(location.pathname);
    }
    
    return (
        <>
            <div className="my-4 sm:my-6">
                {effectiveBackLink && (
                    <div className="mb-2 text-center sm:text-left">
                        <Link
                            to={effectiveBackLink}
                            className="text-sm text-gray-500 hover:text-gray-700 hover:underline inline-flex items-center"
                        >
                            <ArrowLeftIcon className="h-4 w-4 mr-1" />
                            Back
                        </Link>
                    </div>
                )}
                <h1 className="text-3xl sm:text-3xl pb-2 text-center tracking-tight text-gray-800">
                    {enableUnderline && themeColor ? (
                        <UnderlinedTag text={title} colorClass={themeColor} size="small" />
                    ) : (
                        title
                    )}
                </h1>
                {subtitle && (
                    <p className="text-sm sm:text-base pb-2 text-center sm:text-left text-gray-500 italic">
                        {subtitleLink ? (
                            <Link
                                to={subtitleLink}
                                className="text-sm sm:text-base pb-2 text-center sm:text-left text-gray-500 italic hover:underline"
                            >
                                {subtitle}
                            </Link>
                        ) : (
                            subtitle
                        )}
                    </p>
                )}
            </div>
            <hr className="mb-4"></hr>
        </>
    );
};

// Export PageTitle as the default header component
// For backward compatibility during migration, you can alias:
// const ArticleTitle = PageTitle;
// const PageHeader = PageTitle;


export const H2Heading = ({ children, classes = "" }) => (
    <h2 className={`flex text-center items-center justify-center text-lg sm:text-xl mb-2 tracking-tight ${classes}`}>
        {children}
    </h2>
);


export const H3Heading = ({ children, classes = "" }) => (
    <h3 className={`flex items-center justify-center text-base sm:text-lg mb-2 tracking-tight ${classes}`}>
        {children}
    </h3>
);
